/**
 * Declarative field schema for every editable section type.
 *
 * Used by SectionEditor to render the correct form fields, and by SectionRow
 * to display a human-readable section label.
 *
 * Prop keys MUST exactly match the backend validator and the public section
 * component prop names — a mismatch silently drops edits.
 *
 * Field kinds:
 *   text      → TextField (single line)
 *   textarea  → TextField multiline
 *   url       → TextField (url type)
 *   bool      → Switch
 *   list      → editable ordered list of strings (add/remove/reorder)
 *   itemList  → repeatable sub-form cards (add/edit/remove/reorder); itemFields defines sub-fields
 *   iconSelect → Select from the 12-icon allowlist
 *   select    → Select with explicit options array
 */

export const ICON_ALLOWLIST = [
  'EventSeatOutlined',
  'PaidOutlined',
  'PersonOffOutlined',
  'MenuBookOutlined',
  'Groups',
  'CurrencyRupee',
  'QrCode2',
  'WhatsApp',
  'QrCode2Outlined',
  'AccountBalanceWalletOutlined',
  'InsightsOutlined',
  'SmartphoneOutlined',
];

const DYNAMIC_OPTIONS = [
  { value: '', label: 'None (use value as-is)' },
  { value: 'libraries', label: 'Live library count' },
  { value: 'cities', label: 'Live cities count' },
];

/** @type {Record<string, { label: string, fields: import('./SectionEditor').FieldDef[] }>} */
const sectionFieldSchemas = {
  scrollProgressBar: {
    label: 'Scroll Progress Bar',
    fields: [], // visibility-only; no editable props
  },

  marketingHeader: {
    label: 'Marketing Header',
    fields: [
      { key: 'ctaLabel', label: 'CTA Button Label', kind: 'text' },
      { key: 'loginLabel', label: 'Login Button Label', kind: 'text' },
      { key: 'showLogin', label: 'Show Login Button', kind: 'bool' },
    ],
  },

  hero: {
    label: 'Hero',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'rotatingWords', label: 'Rotating Words', kind: 'list' },
      { key: 'headingPrefix', label: 'Heading Prefix', kind: 'text' },
      { key: 'headingSuffix', label: 'Heading Suffix', kind: 'text' },
      { key: 'body', label: 'Body Text', kind: 'textarea' },
      { key: 'primaryCtaLabel', label: 'Primary CTA Label', kind: 'text' },
      { key: 'secondaryCtaLabel', label: 'Secondary CTA Label', kind: 'text' },
      { key: 'whatsappUrl', label: 'WhatsApp URL', kind: 'url' },
    ],
  },

  socialProof: {
    label: 'Social Proof Strip',
    fields: [
      { key: 'liveLabel', label: 'Live Label', kind: 'text' },
      {
        key: 'stats',
        label: 'Stats',
        kind: 'itemList',
        itemFields: [
          { key: 'value', label: 'Value', kind: 'text' },
          { key: 'label', label: 'Label', kind: 'text' },
          { key: 'dynamic', label: 'Dynamic Source', kind: 'select', options: DYNAMIC_OPTIONS },
          { key: 'suffix', label: 'Suffix', kind: 'text' },
        ],
      },
    ],
  },

  pricing: {
    label: 'Pricing',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea' },
      { key: 'points', label: 'Bullet Points', kind: 'list' },
      { key: 'ctaLabel', label: 'CTA Label', kind: 'text' },
    ],
  },

  problems: {
    label: 'Problems',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      {
        key: 'items',
        label: 'Problem Items',
        kind: 'itemList',
        itemFields: [
          { key: 'icon', label: 'Icon', kind: 'iconSelect' },
          { key: 'title', label: 'Title', kind: 'text' },
          { key: 'description', label: 'Description', kind: 'textarea' },
        ],
      },
    ],
  },

  benefits: {
    label: 'Benefits',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      {
        key: 'items',
        label: 'Benefit Items',
        kind: 'itemList',
        itemFields: [
          { key: 'icon', label: 'Icon', kind: 'iconSelect' },
          { key: 'title', label: 'Title', kind: 'text' },
          { key: 'description', label: 'Description', kind: 'textarea' },
        ],
      },
    ],
  },

  features: {
    label: 'Features',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea' },
      {
        key: 'items',
        label: 'Feature Items',
        kind: 'itemList',
        itemFields: [
          { key: 'icon', label: 'Icon', kind: 'iconSelect' },
          { key: 'title', label: 'Title', kind: 'text' },
          { key: 'description', label: 'Description', kind: 'textarea' },
        ],
      },
    ],
  },

  howItWorks: {
    label: 'How It Works',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea' },
      {
        key: 'steps',
        label: 'Steps',
        kind: 'itemList',
        itemFields: [
          { key: 'number', label: 'Step Number', kind: 'text' },
          { key: 'title', label: 'Title', kind: 'text' },
          { key: 'description', label: 'Description', kind: 'textarea' },
        ],
      },
    ],
  },

  priceTiers: {
    label: 'Price Tiers',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea' },
      { key: 'footnote', label: 'Footnote (small print)', kind: 'textarea' },
      { key: 'ctaLabel', label: 'CTA Button Label', kind: 'text' },
      {
        key: 'tiers',
        label: 'Pricing Tiers',
        kind: 'itemList',
        itemFields: [
          { key: 'name', label: 'Tier Name', kind: 'text' },
          { key: 'price', label: 'Price (₹, numbers only)', kind: 'text' },
          { key: 'billing_period', label: 'Billing Period (e.g. per year)', kind: 'text' },
          { key: 'badge', label: 'Badge (optional, e.g. Most popular)', kind: 'text' },
          { key: 'highlight', label: 'Highlight as recommended', kind: 'bool' },
          { key: 'features', label: 'Features', kind: 'list' },
        ],
      },
    ],
  },

  websiteAddon: {
    label: 'Website Add-on',
    fields: [
      { key: 'eyebrowChip', label: 'Eyebrow Chip', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'body', label: 'Body', kind: 'textarea' },
      { key: 'checklistMain', label: 'Main Checklist Items', kind: 'list' },
      { key: 'cardLabel', label: 'Card Label', kind: 'text' },
      { key: 'priceText', label: 'Price Text', kind: 'text' },
      { key: 'pricePeriod', label: 'Price Period', kind: 'text' },
      { key: 'checklistPrice', label: 'Price Checklist Items', kind: 'list' },
      { key: 'ctaLabel', label: 'CTA Label', kind: 'text' },
      { key: 'examplesEyebrow', label: 'Examples Eyebrow', kind: 'text' },
      { key: 'examplesTitle', label: 'Examples Title', kind: 'text' },
      { key: 'examplesBody', label: 'Examples Body', kind: 'textarea' },
    ],
  },

  featuredLibraries: {
    label: 'Featured Libraries',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea' },
      {
        key: 'library_ids',
        label: 'Pinned Libraries (optional — leave empty to show top-rated dynamically)',
        kind: 'librarySelect',
      },
    ],
  },

  faq: {
    label: 'FAQ',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea' },
      {
        key: 'items',
        label: 'FAQ Items',
        kind: 'itemList',
        itemFields: [
          { key: 'question', label: 'Question', kind: 'text' },
          { key: 'answer', label: 'Answer', kind: 'textarea' },
        ],
      },
    ],
  },

  signup: {
    label: 'Signup / CTA',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea' },
      { key: 'pitchTitle', label: 'Pitch Title', kind: 'text' },
      { key: 'pitchBody', label: 'Pitch Body', kind: 'textarea' },
      { key: 'whatsappCtaLabel', label: 'WhatsApp CTA Label', kind: 'text' },
      { key: 'whatsappUrl', label: 'WhatsApp URL', kind: 'url' },
      { key: 'trustBullets', label: 'Trust Bullets', kind: 'list' },
    ],
  },

  marketingFooter: {
    label: 'Marketing Footer',
    fields: [
      { key: 'tagline', label: 'Tagline', kind: 'textarea' },
      {
        key: 'productLinks',
        label: 'Product Links',
        kind: 'itemList',
        itemFields: [
          { key: 'label', label: 'Label', kind: 'text' },
          { key: 'href', label: 'URL', kind: 'url' },
        ],
      },
      {
        key: 'companyLinks',
        label: 'Company Links',
        kind: 'itemList',
        itemFields: [
          { key: 'label', label: 'Label', kind: 'text' },
          { key: 'href', label: 'URL', kind: 'url' },
        ],
      },
      {
        key: 'legalLinks',
        label: 'Legal Links',
        kind: 'itemList',
        itemFields: [
          { key: 'label', label: 'Label', kind: 'text' },
          { key: 'href', label: 'URL', kind: 'url' },
          { key: 'title', label: 'Title (optional)', kind: 'text' },
        ],
      },
      { key: 'contactEmail', label: 'Contact Email', kind: 'text' },
      {
        key: 'phones',
        label: 'Phone Numbers',
        kind: 'itemList',
        itemFields: [
          { key: 'label', label: 'Label', kind: 'text' },
          { key: 'href', label: 'Tel URL (e.g. tel:+91...)', kind: 'url' },
        ],
      },
      { key: 'whatsappUrl', label: 'WhatsApp URL', kind: 'url' },
      { key: 'copyright', label: 'Copyright Text', kind: 'text' },
    ],
  },

  // ── Generic section types (new blank pages — NO prefill) ───────────────────

  genericHero: {
    label: 'Generic Hero',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'heading', label: 'Heading', kind: 'text' },
      { key: 'subheading', label: 'Subheading', kind: 'text' },
      { key: 'body', label: 'Body Text', kind: 'textarea' },
      {
        key: 'alignment',
        label: 'Text Alignment',
        kind: 'select',
        options: [
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ],
      },
      { key: 'backgroundColor', label: 'Background Color (CSS value, e.g. #1b365d)', kind: 'text' },
      { key: 'backgroundImage', label: 'Background Image URL', kind: 'url' },
      {
        key: 'buttons',
        label: 'CTA Buttons',
        kind: 'itemList',
        itemFields: [
          { key: 'label', label: 'Button Label', kind: 'text' },
          { key: 'href', label: 'Button URL', kind: 'url' },
        ],
      },
    ],
  },

  genericContent: {
    label: 'Generic Content',
    fields: [
      { key: 'heading', label: 'Heading', kind: 'text' },
      { key: 'body', label: 'Body Text', kind: 'textarea' },
      {
        key: 'alignment',
        label: 'Text Alignment',
        kind: 'select',
        options: [
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ],
      },
    ],
  },

  genericMedia: {
    label: 'Generic Media (Image)',
    fields: [
      { key: 'imageUrl', label: 'Image URL', kind: 'url' },
      { key: 'caption', label: 'Caption (optional)', kind: 'text' },
      {
        key: 'alignment',
        label: 'Alignment',
        kind: 'select',
        options: [
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ],
      },
      {
        key: 'width',
        label: 'Width',
        kind: 'select',
        options: [
          { value: 'full', label: 'Full (1200px)' },
          { value: 'wide', label: 'Wide (900px)' },
          { value: 'narrow', label: 'Narrow (600px)' },
        ],
      },
    ],
  },

  genericItems: {
    label: 'Generic Items (Card Grid)',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'heading', label: 'Heading', kind: 'text' },
      { key: 'subheading', label: 'Subheading', kind: 'textarea' },
      {
        key: 'alignment',
        label: 'Header Alignment',
        kind: 'select',
        options: [
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ],
      },
      {
        key: 'items',
        label: 'Items',
        kind: 'itemList',
        itemFields: [
          { key: 'title', label: 'Title', kind: 'text' },
          { key: 'text', label: 'Body Text', kind: 'textarea' },
          { key: 'imageUrl', label: 'Image URL (optional)', kind: 'url' },
          { key: 'href', label: 'Link URL (optional)', kind: 'url' },
        ],
      },
    ],
  },

  genericCta: {
    label: 'Generic CTA',
    fields: [
      { key: 'heading', label: 'Heading', kind: 'text' },
      { key: 'text', label: 'Body Text', kind: 'textarea' },
      {
        key: 'alignment',
        label: 'Alignment',
        kind: 'select',
        options: [
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ],
      },
      {
        key: 'buttons',
        label: 'Buttons',
        kind: 'itemList',
        itemFields: [
          { key: 'label', label: 'Button Label', kind: 'text' },
          { key: 'href', label: 'Button URL', kind: 'url' },
        ],
      },
    ],
  },

  libraries: {
    label: 'Libraries (Card Grid)',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow Label', kind: 'text' },
      { key: 'title', label: 'Title', kind: 'text' },
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea' },
      {
        key: 'library_ids',
        label: 'Pinned Libraries (optional — leave empty to show top-rated dynamically)',
        kind: 'librarySelect',
      },
    ],
  },

  spacer: {
    label: 'Spacer',
    fields: [
      {
        key: 'size',
        label: 'Size',
        kind: 'select',
        options: [
          { value: 'small', label: 'Small' },
          { value: 'medium', label: 'Medium' },
          { value: 'large', label: 'Large' },
        ],
      },
      { key: 'divider', label: 'Show Divider Line', kind: 'bool' },
    ],
  },
};

export default sectionFieldSchemas;
